import { CanadaPostManifestMode } from "@redotech/redo-fulfillment-carrier-account-common/canada-post";
import { Carriers } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  Carrier,
  DhlEcsDistributionCenter,
} from "@redotech/redo-fulfillment-carrier-common/carriers-and-services";
import * as zExt from "@redotech/rpc/ext";
import { assertNever } from "@redotech/util/type";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { RateProvider } from "./outbound-labels";

export enum UspsDirectRateType {
  REDO_RATES = "redo_rates",
  MERCHANT_RATES = "merchant_rates",
}

export enum UspsOAuthStatus {
  PENDING = "pending",
  APPROVED = "approved",
}

export const CANADA_POST_API_PLATFORMS = [
  "legacy",
  "developer_portal",
] as const;

// https://docs.easypost.com/docs/carrier-types
export enum EasyPostCarrierType {
  DHL_ECS = "DhlEcsAccount",
  DHL_EXPRESS = "DhlExpressAccount",
  UPS = "UpsAccount",
  UPS_SUREPOST = "UpsSurepostAccount",
  UPS_MAIL_INNOVATIONS = "UpsMailInnovationsAccount",
  USPS = "UspsAccount",
  FEDEX_CROSS_BORDER = "FedexCrossBorderAccount",
  FEDEX = "FedexAccount",
  FEDEX_SMARTPOST = "FedexSmartpostAccount",
  VEHO = "VehoAccount",
  APC = "ApcAccount",
  FIRST_MILE = "FirstMileConciseAccount",
  AUSTRALIA_POST = "AustraliaPostAccount",
  CANADA_POST = "CanadaPostAccount",
  ROYAL_MAIL = "RoyalMailAccount",
  ROYAL_MAIL_V3 = "RoyalMailV3Account",
  SEKO_OMNIPARCEL = "OmniParcelAccount",
  GLS = "GsoAccount",
  ASENDIA = "AsendiaUsaAccount",
  SPEE_DEE = "SpeedeeAccount",
  AMAZON = "AmazonShippingAccount",
  USPS_SHIP = "UspsShipAccount",
  PASSPORT_GLOBAL = "PassportGlobalAccount",
  EPOST_GLOBAL = "RRDonnelleyAccount",
  P2P_GLOBAL = "P2PAccount",
  ONTRAC = "OntracV3Account",
  PUROLATOR = "PurolatorAccount",
  UNIUNI = "UniUniAccount",
  CANPAR = "CanparAccount",
  JITSU = "JitsuAccount",
  GOFO = "ParcllAccount",
}

export enum RedoCarrierName {
  UPS = "UPS",
  UPS_SUREPOST = "UPS SurePost",
  UPS_MAIL_INNOVATIONS = "UPS Mail Innovations",
  DHL_ECS = "DHL eCommerce",
  DHL_EXPRESS = "DHL Express",
  USPS = "USPS",
  FEDEX_CROSS_BORDER = "Fedex Cross Border",
  FEDEX = "FedEx",
  FEDEX_SMARTPOST = "FedEx Smartpost",
  VEHO = "Veho",
  APC = "APC",
  FIRST_MILE = "FirstMile",
  AUSTRALIA_POST = "Australia Post",
  CANADA_POST = "Canada Post",
  CANADA_POST_DIRECT = "Canada Post Direct",
  ROYAL_MAIL = "Royal Mail",
  ROYAL_MAIL_V3 = "Royal Mail v3",
  SEKO_OMNIPARCEL = "OmniParcel - Seko",
  GLS = "GLS",
  ASENDIA = "Asendia",
  AMAZON = "Amazon",
  SPEE_DEE = "SpeeDee",
  USPS_SHIP = "USPS Ship",
  PASSPORT_GLOBAL = "Passport Global",
  EPOST_GLOBAL = "ePost Global",
  P2P_GLOBAL = "P2PGlobal",
  ETA_EXPRESS = "ETA Express",
  USPS_DIRECT = "USPS Direct",
  ONTRAC = "OnTrac",
  PUROLATOR = "Purolator",
  UNIUNI = "UniUni",
  SENDCLOUD = "Sendcloud",
  STALLION_EXPRESS = "Stallion Express",
  ECOM_EXPRESS = "Ecom Express",
  CANPAR_EXPRESS = "Canpar Express",
  CHITCHATS = "ChitChats",
  JITSU = "Jitsu",
  FREIGHTWIRE = "FreightWire",
  GOFO = "GOFO",
}

// This is not a 1:1 mapping - not everything that is a RedoCarrierName is a Carrier
export const redoCarrierNameToCarrier: Record<string, Carrier> = {
  [RedoCarrierName.UPS]: Carriers.UPS,
  [RedoCarrierName.UPS_SUREPOST]: Carriers.UPS_SUREPOST,
  [RedoCarrierName.UPS_MAIL_INNOVATIONS]: Carriers.UPS_MAIL_INNOVATIONS,
  [RedoCarrierName.USPS]: Carriers.USPS,
  [RedoCarrierName.FEDEX]: Carriers.FEDEX,
  [RedoCarrierName.DHL_ECS]: Carriers.DHL_ECS,
  [RedoCarrierName.DHL_EXPRESS]: Carriers.DHL_EXPRESS,
  [RedoCarrierName.FEDEX_SMARTPOST]: Carriers.FEDEX_SMART_POST,
  [RedoCarrierName.FEDEX_CROSS_BORDER]: Carriers.FEDEX,
  [RedoCarrierName.GLS]: Carriers.GLS,
  [RedoCarrierName.ASENDIA]: Carriers.ASENDIA,
  [RedoCarrierName.AUSTRALIA_POST]: Carriers.AUSTRALIA_POST,
  [RedoCarrierName.CANADA_POST]: Carriers.CANADA_POST,
  [RedoCarrierName.CANADA_POST_DIRECT]: Carriers.CANADA_POST,
  [RedoCarrierName.ROYAL_MAIL_V3]: Carriers.ROYAL_MAIL_V3,
  [RedoCarrierName.VEHO]: Carriers.VEHO,
  [RedoCarrierName.APC]: Carriers.APC,
  [RedoCarrierName.FIRST_MILE]: Carriers.FIRST_MILE,
  [RedoCarrierName.SPEE_DEE]: Carriers.SPEE_DEE,
  [RedoCarrierName.AMAZON]: Carriers.AMAZON,
  [RedoCarrierName.USPS_SHIP]: Carriers.USPS,
  [RedoCarrierName.PASSPORT_GLOBAL]: Carriers.PASSPORT_GLOBAL,
  [RedoCarrierName.EPOST_GLOBAL]: Carriers.EPOST_GLOBAL,
  [RedoCarrierName.P2P_GLOBAL]: Carriers.P2P_GLOBAL,
  [RedoCarrierName.ETA_EXPRESS]: Carriers.ETA_EXPRESS,
  [RedoCarrierName.USPS_DIRECT]: Carriers.USPS_SHIP,
  [RedoCarrierName.ONTRAC]: Carriers.ONTRAC,
  [RedoCarrierName.PUROLATOR]: Carriers.PUROLATOR,
  [RedoCarrierName.UNIUNI]: Carriers.UNIUNI,
  [RedoCarrierName.STALLION_EXPRESS]: Carriers.STALLION_EXPRESS,
  [RedoCarrierName.ECOM_EXPRESS]: Carriers.ECOM_EXPRESS,
  [RedoCarrierName.CANPAR_EXPRESS]: Carriers.CANPAR_EXPRESS,
  [RedoCarrierName.GOFO]: Carriers.GOFO,
};

export function isUspsRedoCarrierName(
  redoCarrierName: RedoCarrierName,
): boolean {
  return (
    redoCarrierName === RedoCarrierName.USPS_SHIP ||
    redoCarrierName === RedoCarrierName.USPS ||
    redoCarrierName === RedoCarrierName.USPS_DIRECT
  );
}

// Convert the carrier name that we store on easypost integration utils to the carrier name that we reference elsewhere in the codebase
// From the begining these probably should have been the same, but because they aren't we need this conversion
// Returns undefined for carrier names with no outbound Carrier; callers skip them.
export function merchantCarrierNameToCarrier(
  merchantCarrierName: RedoCarrierName,
): Carrier | undefined {
  switch (merchantCarrierName) {
    case RedoCarrierName.UPS:
      return Carriers.UPS;
    case RedoCarrierName.UPS_SUREPOST:
      return Carriers.UPS_SUREPOST;
    case RedoCarrierName.UPS_MAIL_INNOVATIONS:
      return Carriers.UPS_MAIL_INNOVATIONS;
    case RedoCarrierName.USPS:
      return Carriers.USPS;
    case RedoCarrierName.FEDEX:
      return Carriers.FEDEX;
    case RedoCarrierName.DHL_ECS:
      return Carriers.DHL_ECS;
    case RedoCarrierName.DHL_EXPRESS:
      return Carriers.DHL_EXPRESS;
    case RedoCarrierName.FEDEX_SMARTPOST:
      return Carriers.FEDEX_SMART_POST;
    case RedoCarrierName.FEDEX_CROSS_BORDER:
      return Carriers.FEDEX;
    case RedoCarrierName.GLS:
      return Carriers.GLS;
    case RedoCarrierName.ASENDIA:
      return Carriers.ASENDIA;
    case RedoCarrierName.AUSTRALIA_POST:
      return Carriers.AUSTRALIA_POST;
    case RedoCarrierName.CANADA_POST:
    case RedoCarrierName.CANADA_POST_DIRECT:
      return Carriers.CANADA_POST;
    case RedoCarrierName.ROYAL_MAIL:
    case RedoCarrierName.ROYAL_MAIL_V3:
      return Carriers.ROYAL_MAIL_V3;
    case RedoCarrierName.VEHO:
      return Carriers.VEHO;
    case RedoCarrierName.APC:
      return Carriers.APC;
    case RedoCarrierName.FIRST_MILE:
      return Carriers.FIRST_MILE;
    case RedoCarrierName.SEKO_OMNIPARCEL:
      // not a valid outbound carrier
      return undefined;
    case RedoCarrierName.SPEE_DEE:
      return Carriers.SPEE_DEE;
    case RedoCarrierName.AMAZON:
      return Carriers.AMAZON;
    case RedoCarrierName.USPS_SHIP:
      return Carriers.USPS;
    case RedoCarrierName.PASSPORT_GLOBAL:
      return Carriers.PASSPORT_GLOBAL;
    case RedoCarrierName.EPOST_GLOBAL:
      return Carriers.EPOST_GLOBAL;
    case RedoCarrierName.P2P_GLOBAL:
      return Carriers.P2P_GLOBAL;
    case RedoCarrierName.ETA_EXPRESS:
      return Carriers.ETA_EXPRESS;
    case RedoCarrierName.USPS_DIRECT:
      return Carriers.USPS;
    case RedoCarrierName.ONTRAC:
      return Carriers.ONTRAC;
    case RedoCarrierName.PUROLATOR:
      return Carriers.PUROLATOR;
    case RedoCarrierName.UNIUNI:
      return Carriers.UNIUNI;
    case RedoCarrierName.STALLION_EXPRESS:
      return Carriers.STALLION_EXPRESS;
    case RedoCarrierName.ECOM_EXPRESS:
      return Carriers.ECOM_EXPRESS;
    case RedoCarrierName.FREIGHTWIRE:
      return undefined;
    case RedoCarrierName.CHITCHATS:
      return Carriers.CHITCHATS;
    case RedoCarrierName.CANPAR_EXPRESS:
      return Carriers.CANPAR_EXPRESS;
    case RedoCarrierName.SENDCLOUD:
      // returns-only
      return undefined;
    case RedoCarrierName.JITSU:
      // tracking-only
      return undefined;
    case RedoCarrierName.GOFO:
      return Carriers.GOFO;
    default:
      assertNever(merchantCarrierName);
  }
}

export enum CarrierAccountType {
  /**
   * Merchant carrier accounts are accounts that belong to the merchant that they want to get rates through
   */
  MERCHANT = "MERCHANT",
  /**
   * Redo child carrier accounts are accounts that are owned by Redo but have different credentials per merchant.
   * Rates through these accounts have spread applied to them and are billed through Redo like standard Redo rates
   */
  REDO_CHILD = "REDO_CHILD",
}

export function supportedCarriersForCarrierAccountType(
  carrierAccountType: CarrierAccountType,
): RedoCarrierName[] {
  switch (carrierAccountType) {
    case CarrierAccountType.MERCHANT:
      return Object.values(RedoCarrierName).filter(
        (redoCarrierName) =>
          // Hidden until fully working TODO(@CanyonTurtle)
          redoCarrierName !== RedoCarrierName.ROYAL_MAIL &&
          // FirstMile is only supported as a Redo child carrier account
          redoCarrierName !== RedoCarrierName.FIRST_MILE,
      );
    case CarrierAccountType.REDO_CHILD:
      return [RedoCarrierName.GOFO];
    default:
      assertNever(carrierAccountType);
  }
}

export const supportedRedoChildCarriers = [Carriers.GOFO];

export enum EasypostOAuthStatus {
  APPROVED = "approved",
  FAILURE = "failure",
}

export enum FedexCrossBorderHubOrigin {
  FONTANA = "Fontana",
  SADDLE_BROOK = "Saddle Brook",
  BASILDON = "Basildon",
}

// Which Sendcloud platform a carrier account belongs to. Sendcloud runs the EU
// and UK as separate platforms (separate API credentials), so a single account
// maps to exactly one region. Single source of truth for the persisted region
// across the Mongoose schema, the CockroachDB enum, RPC, and rate routing.
export enum SendcloudRegion {
  EU = "EU",
  UK = "UK",
}

function isSendcloudRegion(value: string): value is SendcloudRegion {
  return (Object.values(SendcloudRegion) as string[]).includes(value);
}

/**
 * Resolves a persisted `shipping_account` key. Labels bought before the region
 * was written to the shipment carry no account and were all UK.
 */
export function toSendcloudRegion(
  value: string | null | undefined,
): SendcloudRegion {
  return value && isSendcloudRegion(value) ? value : SendcloudRegion.UK;
}

// Only `active` contracts are shopped/bought against.
export const SendcloudContractStatus = {
  VALIDATING: "validating",
  ACTIVE: "active",
  INACTIVE: "inactive",
  VALIDATION_FAILED: "validation_failed",
} as const;
export type SendcloudContractStatus =
  (typeof SendcloudContractStatus)[keyof typeof SendcloudContractStatus];

// Never carries credentials. Sendcloud is the system of record for contract data.
export const SendcloudCarrierContract = z.object({
  redoCarrierAccountId: z.string(),
  region: createEnum(SendcloudRegion),
  carrierCode: z.string(),
  displayName: z.string(),
  status: createEnum(SendcloudContractStatus),
  maskedAccountNumber: z.string().optional(),
  sendcloudContractId: z.number(),
  createdAt: z.date(),
});
export type SendcloudCarrierContract = z.infer<typeof SendcloudCarrierContract>;

export const EasypostCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.EASYPOST),
  carrierAccountId: z.string(),
  easypostCarrierType: createEnum(EasyPostCarrierType),
  redoCarrierName: createEnum(RedoCarrierName),
  name: z.string(),
  oauthUrl: z.string().optional(),
  oauthStatus: z.string().optional(),
  mfaComplete: z.boolean().optional(),
});

export const EtaExpressCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.ETA_EXPRESS),
  redoCarrierName: createEnum(RedoCarrierName),
  name: z.string(),
  carrierAccountId: z.string(),
});

export const FedExDirectCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.FEDEX),
  redoCarrierName: createEnum(RedoCarrierName),
  authenticated: z.boolean(),
  name: z.string(),
  failedAt: z.date().optional(),
  canUseMfaBackup: z.boolean(),
  carrierAccountId: z.string(),
});

export const UspsDirectCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.USPS),
  redoCarrierName: createEnum(RedoCarrierName),
  name: z.string(),
  oauthStatus: createEnum(UspsOAuthStatus),
  rateType: createEnum(UspsDirectRateType),
  contractRatesLinked: z.boolean(),
  carrierAccountId: z.string(),
});

export const UniUniDirectCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.UNIUNI),
  redoCarrierName: createEnum(RedoCarrierName),
  name: z.string(),
  carrierAccountId: z.string(),
  pickupWarehouseId: z.number(),
  customerNumber: z.string(),
});

export const DhlEcommerceDirectCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.DHL_ECOMMERCE),
  redoCarrierName: createEnum(RedoCarrierName),
  name: z.string(),
  carrierAccountId: z.string(),
  distributionCenter: createEnum(DhlEcsDistributionCenter),
  pickupId: z.string(),
});

export const StallionExpressDirectCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.STALLION_EXPRESS),
  redoCarrierName: createEnum(RedoCarrierName),
  name: z.string(),
  carrierAccountId: z.string(),
});

export const EcomExpressDirectCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.ECOM_EXPRESS),
  redoCarrierName: createEnum(RedoCarrierName),
  name: z.string(),
  carrierAccountId: z.string(),
  accountNumber: z.string(),
});

export const FreightWireDirectCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.FREIGHTWIRE),
  redoCarrierName: z.literal(RedoCarrierName.FREIGHTWIRE),
  name: z.string(),
  carrierAccountId: z.string(),
  /** FreightWire's per-account customer ID. */
  accountNumber: z.string(),
});

export const SendcloudCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.SENDCLOUD),
  redoCarrierName: createEnum(RedoCarrierName),
  name: z.string(),
  region: createEnum(SendcloudRegion),
  carrierAccountId: z.string(),
  authenticated: z.boolean(),
});

export const ChitChatsDirectCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.CHITCHATS),
  redoCarrierName: z.literal(RedoCarrierName.CHITCHATS),
  name: z.string(),
  carrierAccountId: z.string(),
});

export const CanadaPostDirectCarrierAccount = z.object({
  _id: zExt.objectId(),
  provider: z.literal(RateProvider.CANADA_POST),
  redoCarrierName: z.literal(RedoCarrierName.CANADA_POST_DIRECT),
  name: z.string(),
  carrierAccountId: z.string(),
  customerNumber: z.string(),
  contractNumber: z.string(),
  apiPlatform: z.enum(CANADA_POST_API_PLATFORMS),
  manifestMode: createEnum(CanadaPostManifestMode, {
    enumName: "CanadaPostManifestMode",
  }),
});

const CarrierAccount_ = z.discriminatedUnion("provider", [
  EasypostCarrierAccount,
  EtaExpressCarrierAccount,
  FedExDirectCarrierAccount,
  UspsDirectCarrierAccount,
  UniUniDirectCarrierAccount,
  DhlEcommerceDirectCarrierAccount,
  SendcloudCarrierAccount,
  StallionExpressDirectCarrierAccount,
  EcomExpressDirectCarrierAccount,
  FreightWireDirectCarrierAccount,
  ChitChatsDirectCarrierAccount,
  CanadaPostDirectCarrierAccount,
]);
export interface CarrierAccountSchema extends z.ZodType<ICarrierAccount> {}
export const CarrierAccount: CarrierAccountSchema = CarrierAccount_;
export type ICarrierAccount = {} & z.infer<typeof CarrierAccount_>;
